//Library imports
import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  Typography,
  notification,
  Space,
  Popconfirm,
  Layout,
  Tabs,
  Badge,
} from 'antd';
import dayjs from 'dayjs';

//Component imports
import BreadcrumbContainer from '../../../containers/Breadcrumb';
import CustomButton from '../../../components/CustomButton';
import INTERNAL_LINKS from '../../../enums/InternalLinks';
import MESSAGES from '../../../enums/Messages';
import AjaxTable from '../../../components/AjaxTable';

//API imports
import {
  chanteState as editStatus,
  ajaxTable as filterAjaxTable,
  counters as getProjectCounters,
} from '../../../api/module/projects';

//Css imports
import './style.scss';
import { ClockCircleOutlined, PlusOutlined } from '@ant-design/icons';
import { catchExceptionSRate } from '../../../utils/helpers/helpers';

//Constants
const { Title, Paragraph } = Typography;
const { Content } = Layout;
const { TabPane } = Tabs;

const breadcrumbItems = [
  {
    text: 'Proyectos',
  },
];

const initCounters = {
  active: (
    <ClockCircleOutlined style={{ marginLeft: '13px', marginRight: '3px' }} />
  ),
  closed: (
    <ClockCircleOutlined style={{ marginLeft: '13px', marginRight: '3px' }} />
  ),
  administrativeClosed: (
    <ClockCircleOutlined style={{ marginLeft: '13px', marginRight: '3px' }} />
  ),
};

const ProjectsList = ({ perm }) => {
  const location = useLocation();
  const [counters, setCounters] = useState(initCounters);
  const [loadingButton, setLoadingButton] = useState(false);
  const [reloadTable, setReloadTable] = useState(false);
  const [sortBy] = useState('nombre');

  useEffect(() => {
    getCounters();
  }, []);

  const getCounters = async () => {
    try {
      setCounters(initCounters);
      const res = await getProjectCounters();
      setCounters(res.data.results);
    } catch (err) {
      catchExceptionSRate(err);
    }
  };

  const handleStateChange = async (project_id, state) => {
    setLoadingButton(true);
    try {
      await editStatus(project_id, { estado_proyecto_sigla: state });
      notification.success({
        message: MESSAGES.SUCCESS_MSG,
        description: MESSAGES.DESC_SUCCESS_MSG,
        duration: 2,
      });
      setReloadTable({});
      setLoadingButton(false);
      await getCounters();
    } catch (err) {
      catchExceptionSRate(err);
    }
  };

  const handleRT = async (project_id) => {
    await handleStateChange(project_id, 'PY-RT');
  };

  const handleCT = async (project_id) => {
    await handleStateChange(project_id, 'PY-CT');
  };

  const handleCA = async (project_id) => {
    await handleStateChange(project_id, 'PY-CAJ');
  };

  const handleRA = async (project_id) => {
    await handleStateChange(project_id, 'PY-RAJ');
  };

  const columns = [
    {
      title: 'Nombre Proyecto',
      dataIndex: 'nombre',
    },
    {
      title: 'Cliente-Aliado',
      dataIndex: 'cliente_nombre',
      key: 'cliente_nombre',
    },
    {
      title: 'Organización ejecutora',
      dataIndex: 'organizacion_nombre',
      key: 'organizacion_nombre',
      align: 'center',
    },
    {
      title: 'Tipo',
      dataIndex: 'tipo_proyecto_nombre',
      key: 'tipo_proyecto_nombre',
    },
    {
      title: 'Fecha Creación SIGA',
      dataIndex: 'created_at',
      align: 'center',
      render: (_, record) => dayjs(record.created_at).format('YYYY-MM-DD'),
    },
    {
      title: 'Fecha Inicio',
      dataIndex: 'fecha_inicio',
      render: (_, record) => dayjs(record.fecha_inicio).format('YYYY-MM-DD'),
    },
    {
      title: 'Fecha Fin',
      dataIndex: 'fecha_fin',
      render: (_, record) => dayjs(record.fecha_fin).format('YYYY-MM-DD'),
    },
  ];

  const columnsActive = [
    ...columns,
    {
      title: 'Acciones',
      dataIndex: 'acciones',
      align: 'center',
      fixed: 'right',
      render: (_, record) => (
        <Space>
          <CustomButton
            type="default"
            htmlType={'button'}
            className={'ant-btn-info'}
            loading={loadingButton}
            text={
              <Link to={`${INTERNAL_LINKS.PROJECT}/${record.id}`}>Editar</Link>
            }
          />
          <CustomButton
            danger
            type={'default'}
            loading={loadingButton}
            text={
              <Popconfirm
                title="¿Está seguro(a) de que desea realizar el cierre técnico de este proyecto?"
                onConfirm={() => handleCT(record.id)}
                okText="Si"
                cancelText="No"
              >
                Cierre técnico
              </Popconfirm>
            }
          />
        </Space>
      ),
    },
  ];

  const columnsClosed = [
    ...columns,
    {
      title: 'Acciones',
      dataIndex: 'acciones',
      align: 'center',
      fixed: 'right',
      render: (_, record) => (
        <Space>
          <CustomButton
            htmlType={'button'}
            loading={loadingButton}
            text={
              <Popconfirm
                title="¿Está seguro(a) de que desea realizar la reactivación técnica de este proyecto?"
                onConfirm={() => handleRT(record.id)}
                okText="Si"
                cancelText="No"
              >
                Reactivar técnico
              </Popconfirm>
            }
          />
          <CustomButton
            danger
            type={'default'}
            loading={loadingButton}
            htmlType={'button'}
            text={
              <Popconfirm
                title="¿Está seguro(a) de que desea realizar el cierre administrativo y jurídico de este proyecto?"
                onConfirm={() => handleCA(record.id)}
                okText="Si"
                cancelText="No"
              >
                Cierre administrativo y jurídico
              </Popconfirm>
            }
          />
          <CustomButton
            type="default"
            loading={loadingButton}
            htmlType={'button'}
            text={
              <Link
                to={`${INTERNAL_LINKS.PROJECT}/ver/${record.id}?closed_type=1`}
              >
                Ver
              </Link>
            }
          />
        </Space>
      ),
    },
  ];

  const columnsAdministrativeClosed = [
    ...columns,
    {
      title: 'Acciones',
      dataIndex: 'acciones',
      align: 'center',
      fixed: 'right',
      render: (_, record) => (
        <Space>
          <CustomButton
            htmlType={'button'}
            loading={loadingButton}
            text={
              <Popconfirm
                title="¿Está seguro(a) de que desea realizar la reactivación administrativa y jurídica de este proyecto? "
                onConfirm={() => handleRA(record.id)}
                okText="Si"
                cancelText="No"
              >
                Reactivar administrativo y jurídico
              </Popconfirm>
            }
          />
          <CustomButton
            type="default"
            loading={loadingButton}
            text={
              <Link
                to={`${INTERNAL_LINKS.PROJECT}/ver/${record.id}?closed_type=2`}
              >
                Ver
              </Link>
            }
          />
        </Space>
      ),
    },
  ];

  return (
    <Content>
      <BreadcrumbContainer items={breadcrumbItems} />
      <Title className={'text-uppercase'} level={2}>
        Módulo de administración de Proyectos
      </Title>
      <Space direction={'vertical'} size={'middle'} style={{ width: '100%' }}>
        {perm.Crear && (
          <CustomButton
            type={'default'}
            className={'ant-btn-info'}
            htmlType="button"
            text={
              <Link to={INTERNAL_LINKS.PROJECT}>
                <PlusOutlined /> Crear nuevo proyecto
              </Link>
            }
          />
        )}
        <Tabs
          type="card"
          destroyInactiveTabPane={true}
          defaultActiveKey={
            location.state && location.state.closed_type != null
              ? location.state.closed_type === 1
                ? 'closed'
                : location.state.closed_type === 2
                  ? 'administrativeClosed'
                  : 'active'
              : 'active'
          }
        >
          <TabPane
            tab={
              <Paragraph>
                Activo
                <Badge showZero count={counters.active} />
              </Paragraph>
            }
            key="active"
          >
            <AjaxTable
              columns={columnsActive}
              pageSize={10}
              scroll={{ x: 'max-content' }}
              tableTitle={'Listado de proyectos activos'}
              endpoint={filterAjaxTable}
              sortBy={sortBy}
              reloadSource={reloadTable}
              query={`/?filter[estado_proyecto__sigla__in]=PY-A,PY-RT`}
              exportCSV
              searchInput
            />
          </TabPane>
          <TabPane
            tab={
              <Paragraph>
                Cerrado técnico
                <Badge showZero count={counters.closed} />
              </Paragraph>
            }
            key="closed"
          >
            <AjaxTable
              columns={columnsClosed}
              pageSize={10}
              scroll={{ x: 'max-content' }}
              tableTitle={'Listado de proyectos cerrados técnico'}
              endpoint={filterAjaxTable}
              sortBy={sortBy}
              reloadSource={reloadTable}
              query={`/?filter[estado_proyecto__sigla__in]=PY-CT,PY-RAJ`}
              exportCSV
              searchInput
            />
          </TabPane>
          <TabPane
            tab={
              <Paragraph>
                Cerrado administrativo y jurídico
                <Badge showZero count={counters.administrativeClosed} />
              </Paragraph>
            }
            key="administrativeClosed"
          >
            <AjaxTable
              columns={columnsAdministrativeClosed}
              pageSize={10}
              scroll={{ x: 'max-content' }}
              tableTitle={
                'Listado de proyectos cerrados administrativo y jurídico'
              }
              endpoint={filterAjaxTable}
              sortBy={sortBy}
              reloadSource={reloadTable}
              query={`/?filter[estado_proyecto__sigla]=PY-CAJ`}
              exportCSV
              searchInput
            />
          </TabPane>
        </Tabs>
      </Space>
    </Content>
  );
};

export default ProjectsList;
